/**
 * Imperative read/write helpers for localStorage keys defined in `keys.js`.
 *
 * `usePersistedState` covers the reactive case (returns a ref synced to
 * `pf_<key>`). Some consumers — composables that expose imperative
 * `getX()` / `setX()` APIs, .vue pages that read once at mount, etc. —
 * don't fit that reactive model. They used to call `window.localStorage`
 * directly using the full `pf_*` keys from `keys.js`; this module gives
 * them a single sanctioned wrapper so the drift report can keep flagging
 * direct localStorage access in app code.
 *
 * Why a separate helper from `usePersistedState`:
 * - This module is framework-agnostic (no `ref`/`watch` import).
 * - Callers pass a *full* key (from `keys.js`), not the `pf_` suffix.
 * - Errors are swallowed (private mode, quota exceeded). Failures fall
 *   back to the default value silently.
 *
 * Stored format: JSON for `readJsonItem` / `writeJsonItem` (forward-
 * compatible with `usePersistedState`'s format). `readRawItem` /
 * `writeRawItem` keep the legacy raw-string format for a handful of
 * historical keys that store unquoted strings (e.g. a config id).
 */

function isStorageAvailable() {
  return typeof window !== 'undefined' && !!window.localStorage;
}

/**
 * Read a JSON-encoded value. Returns `defaultValue` on missing key,
 * unavailable storage, or parse failure.
 */
export function readJsonItem(key, defaultValue) {
  if (!isStorageAvailable()) return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}

/**
 * Write a JSON-encoded value. Silently no-ops on quota / private mode.
 */
export function writeJsonItem(key, value) {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota / private mode
  }
}

/**
 * Read a raw (unquoted) string. For legacy keys that store a value
 * without JSON encoding — typically a single id or token.
 */
export function readRawItem(key, defaultValue = null) {
  if (!isStorageAvailable()) return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? defaultValue : raw;
  } catch {
    return defaultValue;
  }
}

/**
 * Write a raw (unquoted) string. Use this only when migrating a legacy
 * raw-string consumer would lose user data on the next read; new code
 * should use `writeJsonItem`.
 */
export function writeRawItem(key, value) {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // quota / private mode
  }
}

/**
 * Remove a key. No-op if missing or storage unavailable.
 */
export function removeItem(key) {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // quota / private mode
  }
}

// ─── sessionStorage variants ─────────────────────────────────────────────
// sessionStorage is the right tool for cross-page handoff state inside a
// single tab (e.g. "user just submitted a catalog request → success page
// reads the receipt") and for the auth tokens that survive a refresh but
// not a tab close. The shape mirrors the localStorage helpers above so
// callers route through a single sanctioned wrapper instead of touching
// `window.sessionStorage` directly.

function isSessionStorageAvailable() {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

export function readSessionJsonItem(key, defaultValue) {
  if (!isSessionStorageAvailable()) return defaultValue;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}

export function writeSessionJsonItem(key, value) {
  if (!isSessionStorageAvailable()) return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // quota / private mode
  }
}

/**
 * Read a raw session-storage value (no JSON parse). Used for auth tokens
 * stored unquoted (`accessToken`, etc.) — see OUT_OF_NAMESPACE_KEYS in
 * keys.js.
 */
export function readSessionRawItem(key, defaultValue = null) {
  if (!isSessionStorageAvailable()) return defaultValue;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw === null ? defaultValue : raw;
  } catch {
    return defaultValue;
  }
}

export function removeSessionItem(key) {
  if (!isSessionStorageAvailable()) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // quota / private mode
  }
}
